import { mergeFontFamilies } from '@creative/font-families.utils';
import { deserializeFontFamilies } from '@data/deserialization/font-families';
import { IFontFamily } from '@domain/font-families';
import { IAsyncState } from '@domain/store/async';
import { createReducer, on } from '@ngrx/store';
import { ExternalFontFamilyDto } from '@studio/domain/api/font-manager.types';
import * as FontFamiliesActions from './font-families.actions';

export const FONT_FAMILIES_FEATURE_KEY = 'fontFamilies';

export interface FontFamiliesPartialState {
    readonly [FONT_FAMILIES_FEATURE_KEY]: FontFamiliesState;
}

export interface FontFamiliesState extends IAsyncState {
    fontFamiliesFromBrand: IFontFamily[];
    fontFamiliesFromCreativeSet: IFontFamily[];

    failedToLoadFontStyleIds: string[];

    externalFontsResult?: ExternalFontFamilyDto[];
    importedExternalFonts?: IFontFamily[];

    fontManagerState: 'loading' | 'loaded' | 'failed';
}

export const initialState: FontFamiliesState = {
    loaded: false,
    fontFamiliesFromBrand: [],
    fontFamiliesFromCreativeSet: [],
    externalFontsResult: undefined,
    importedExternalFonts: undefined,

    failedToLoadFontStyleIds: [],

    fontManagerState: 'loading'
};

export const reducer = createReducer<FontFamiliesState>(
    initialState,

    on(
        FontFamiliesActions.loadFontFamiliesOfBrand,
        FontFamiliesActions.searchExternalFonts,
        FontFamiliesActions.importExternalFonts,
        (state): FontFamiliesState => ({
            ...state,
            loaded: state.fontManagerState === 'failed', // if state failed, mark as loaded
            error: undefined
        })
    ),

    on(
        FontFamiliesActions.loadFontFamiliesOfBrandSuccess,
        FontFamiliesActions.searchExternalFontsSuccess,
        FontFamiliesActions.importExternalFontsSuccess,
        (state): FontFamiliesState => ({
            ...state,
            loaded: true,
            error: undefined
        })
    ),
    on(FontFamiliesActions.loadFontFamiliesOfBrandSuccess, state => ({
        ...state,
        fontManagerState: 'loaded'
    })),
    on(FontFamiliesActions.loadFontFamiliesOfBrandFailure, state => ({
        ...state,
        fontManagerState: 'failed'
    })),
    on(
        FontFamiliesActions.loadFontFamiliesOfBrandSuccess,
        (state, { fontFamilies }): FontFamiliesState => ({
            ...state,
            fontFamiliesFromBrand: fontFamilies
        })
    ),
    on(
        FontFamiliesActions.searchExternalFontsSuccess,
        (state, { response }): FontFamiliesState => ({
            ...state,
            externalFontsResult: response.items
        })
    ),
    on(FontFamiliesActions.importExternalFontsSuccess, (state, { response }): FontFamiliesState => {
        const parsedFontFamilies = deserializeFontFamilies(response.fontFamilies);
        return {
            ...state,
            fontFamiliesFromBrand: [...state.fontFamiliesFromBrand, ...parsedFontFamilies],
            importedExternalFonts: parsedFontFamilies
        };
    }),

    on(
        FontFamiliesActions.loadFontFamiliesOfBrandFailure,
        FontFamiliesActions.searchExternalFontsFailure,
        FontFamiliesActions.importExternalFontsFailure,
        (state, { error }): FontFamiliesState => ({
            ...state,
            loaded: true,
            error
        })
    ),
    on(FontFamiliesActions.FontByStyleId.loadFailure, (state, { error, fontStyleId }) => {
        return {
            ...state,
            loaded: true,
            error,
            failedToLoadFontStyleIds: [...state.failedToLoadFontStyleIds, fontStyleId]
        };
    }),

    on(
        FontFamiliesActions.setFontFamiliesOfCreativeSet,
        (state, { fontFamilies }): FontFamiliesState => ({
            ...state,
            loaded: true,
            error: undefined,
            fontFamiliesFromCreativeSet: fontFamilies
        })
    ),

    on(
        FontFamiliesActions.fontFamiliesShowcaseCreativeSetActions.loadShowcaseSuccess,
        (state, { fonts }): FontFamiliesState => ({
            ...state,
            loaded: true,
            error: undefined,
            fontFamiliesFromCreativeSet: fonts
        })
    ),

    on(
        FontFamiliesActions.addFontFamilies,
        (state, { fontFamilies }): FontFamiliesState => ({
            ...state,
            fontFamiliesFromCreativeSet: mergeFontFamilies(
                fontFamilies,
                state.fontFamiliesFromCreativeSet
            )
        })
    ),
    on(FontFamiliesActions.resetExternalFonts, state => ({
        ...state,
        externalFontsResult: undefined
    })),
    on(FontFamiliesActions.resetImportedFonts, state => ({
        ...state,
        importedExternalFonts: undefined
    })),

    on(FontFamiliesActions.FontByStyleId.loadSuccess, (state, { fontFamily, fontStyleId }) => {
        return {
            ...state,
            failedToLoadFontStyleIds: state.failedToLoadFontStyleIds.filter(id => id !== fontStyleId),
            fontFamiliesFromCreativeSet: mergeFontFamilies(state.fontFamiliesFromCreativeSet, [
                fontFamily
            ])
        };
    })
);
